import { formatPercent } from "@/v2-common/utils/utils.formatting"
import {
  LAYER_VIEW_DRIFT_TAB,
  LAYER_VIEW_TABS_ID,
} from "@/v2-backoffice-client/layer/layer.constants"
import type { LayerDriftFragment } from "@/v2-backoffice-client/layer/drift/__types__/layer.drift.fragment"

// SeverityLabel narrows the open `string` GraphQL type into the three
// categories the classifier produces. The backend will only ever emit
// these values; the cast in groupBySeverity guards against future
// drift between the two sides.
export type SeverityLabel = "breaking" | "warning" | "info"

export const SEVERITY_ORDER: SeverityLabel[] = ["breaking", "warning", "info"]

// Convenience alias: the per-entry shape the table iterates over.
// Re-exported here so component files don't have to dig into the
// generated fragment types every time.
export type LayerDriftEntry = LayerDriftFragment["Entries"][number]

/**
 * Opens a layer's Drift tab from wherever the operator is standing.
 *
 * Uses `>>?` so the layer sheet pushes onto the view-sheet stack rather
 * than replacing the uptime sheet underneath it — drift is resolved in
 * the configurator, but the incident that sent them there is still what
 * they are working on, and the breadcrumb takes them back to it.
 */
export function getLayerDriftSheetHref(identifier: string): string {
  const tab = `${LAYER_VIEW_TABS_ID}-tab=${LAYER_VIEW_DRIFT_TAB}`
  return `>>?v=layer:${encodeURIComponent(identifier)}&${tab}`
}

// groupBySeverity splits the flat entry list into severity buckets in
// descending severity order so the table renders breaking first, then
// warning, then info. Empty buckets are dropped so the section header
// only appears when there's at least one entry to show.
export function groupBySeverity(
  entries: LayerDriftEntry[],
): { severity: SeverityLabel; entries: LayerDriftEntry[] }[] {
  const buckets: Record<SeverityLabel, LayerDriftEntry[]> = {
    breaking: [],
    warning: [],
    info: [],
  }
  for (const entry of entries) {
    const sev = (entry.Severity as SeverityLabel) || "info"
    const bucket = buckets[sev] ?? buckets.info
    bucket.push(entry)
  }
  return SEVERITY_ORDER.filter((s) => buckets[s].length > 0).map(
    (severity) => ({ severity, entries: buckets[severity] }),
  )
}

// pluralChanges renders the "(N changes)" / "(1 change)" suffix the
// header and section labels share. Tiny on its own but pulled out so
// the wording stays consistent if we ever switch to "modifications"
// or similar.
export function pluralChanges(count: number): string {
  return count === 1 ? "1 change" : `${count} changes`
}

// CountSeverity is the open `string` GraphQL enum the backend ships
// for `LayerCountDrift.Severity`. The fixed thresholds in
// Insert_Backoffice_LayerCountSnapshots can only emit one of these
// four values, so the type is closed on the frontend even though the
// schema models it as a string. "none" exists so the union maps
// 1-to-1 onto the latched `HasCountDrift` flag.
export type CountSeverity = "none" | "info" | "warning" | "breaking"

// Coerce the open string from the API into our closed enum. Anything
// unrecognised collapses to "none" so a future severity tier doesn't
// crash the renderer -- it just falls into the "no drift" branch
// until the frontend learns about it.
export function asCountSeverity(s: string | null | undefined): CountSeverity {
  switch (s) {
    case "breaking":
    case "warning":
    case "info":
      return s
    default:
      return "none"
  }
}

// Format a baseline → current count delta as either a percentage
// (when the baseline is positive) or as an absolute drop (when the
// baseline is zero). Returns null when either value is missing so
// the caller can skip the line entirely instead of rendering "—%".
export function formatCountDelta(
  baseline: number | null | undefined,
  current: number | null | undefined,
): { ratio: number | null; pct: string; absolute: number } | null {
  if (baseline == null || current == null) return null
  const absolute = current - baseline
  if (baseline <= 0) {
    return { ratio: null, pct: "—", absolute }
  }
  const ratio = current / baseline
  // Two-decimal pct for tiny moves, one for everything else; this
  // matches the precision a human eye actually uses when scanning
  // a row ("96.4% of baseline" reads cleaner than "96%").
  const pctDelta = (ratio - 1) * 100
  const sign = pctDelta > 0 ? "+" : ""
  const pct =
    Math.abs(pctDelta) < 1
      ? `${sign}${formatPercent(pctDelta, 2)}`
      : `${sign}${formatPercent(pctDelta, 1)}`
  return { ratio, pct, absolute }
}
